import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Card, 
  Button, 
  message, 
  Spin, 
  Typography, 
  Divider, 
  Select, 
  Form, 
  Input, 
  InputNumber, 
  DatePicker, 
  Switch, 
  Space,
  Row,
  Col,
  Alert,
  Empty,
  Collapse,
  Modal,
  Tabs,
  Tag,
  Checkbox
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, SendOutlined, FormOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { getAuthHeaders } from '../utils/authHeaders';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface OrderFormData {
  orderId: string;
  demographicsId: string;
  orderStatus: string;
  orderItems: Array<{
    type: 'Primary' | 'Secondary' | 'Ancillary';
    item: {
      productId: number;
      productName: string;
      variantSize?: string;
      productCategory: string;
      price: number;
    };
    placeOfService: string;
    quantity: number;
    renderingProvider: string;
    frequency: string;
    days: number;
    incisions: string;
  }>;
  patientInfo: {
    firstName: string;
    lastName: string;
    doctorName: string;
    procedureName: string;
    surgeryDate: string;
    dateOfBirth?: string;
    gender?: string;
  };
  createdAt: string;
  lastModifiedDate: string;
}


interface ProcedureTemplate {
  procedureName: string;
  [key: string]: any;
}

interface TemplateFormData {
  [key: string]: any;
}

interface Doctor {
  employeeId: string;
  name: string;
  specializations: string[];
  designation: string;
}

const SubmitOrderPage: React.FC = () => {
  const { orderId, demographicsId } = useParams<{ orderId?: string; demographicsId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  
  // Check if we're in edit mode
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get('mode') === 'edit';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orderData, setOrderData] = useState<OrderFormData | null>(null);
  const [formData, setFormData] = useState<any>({});
  
  // Template-related state
  const [selectedDoctor, setSelectedDoctor] = useState<string>('');
  const [availableProcedures, setAvailableProcedures] = useState<string[]>([]);
  const [selectedProcedure, setSelectedProcedure] = useState<string>('');
  const [originalProcedure, setOriginalProcedure] = useState<string>(''); // Store original procedure for comparison
  
  // Doctors state
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [procedureTemplate, setProcedureTemplate] = useState<ProcedureTemplate | null>(null);
  const [templateFormData, setTemplateFormData] = useState<TemplateFormData>({});
  const [templateLoading, setTemplateLoading] = useState(false);
  
  // Dynamic incision fields state
  const [dynamicIncisionFields, setDynamicIncisionFields] = useState<Record<string, any>>({});
  const [isAddIncisionModalVisible, setIsAddIncisionModalVisible] = useState(false);
  const [newIncisionLabel, setNewIncisionLabel] = useState('');
  const [newIncisionValue, setNewIncisionValue] = useState('');
  
  // Tab management state
  const [activeTab, setActiveTab] = useState<string>('1');
  const [completedTabs, setCompletedTabs] = useState<Set<string>>(new Set());
  
  // OrderItem form state
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [isAddOrderItemModalVisible, setIsAddOrderItemModalVisible] = useState(false);
  const [isEditOrderItemModalVisible, setIsEditOrderItemModalVisible] = useState(false);
  const [editingOrderItemIndex, setEditingOrderItemIndex] = useState<number | null>(null);
  const [newOrderItem, setNewOrderItem] = useState({
    type: 'Primary' as 'Primary' | 'Secondary' | 'Ancillary',
    item: null as any,
    placeOfService: '',
    quantity: 1,
    renderingProvider: '',
    frequency: '',
    days: 1,
    incisions: ''
  });

  // Initialize data on component mount
  useEffect(() => {
    fetchDoctors(); // Fetch doctors on component mount
    if (orderId) {
      if (isEditMode) {
        // In edit mode, fetch complete order data for editing
        fetchOrderDataForEdit(orderId);
      } else {
        // Check if we have record data passed from navigation
        const recordData = location.state?.recordData;
        
        if (recordData) {
          // Use the passed record data
          initializeOrderDataFromRecord(recordData);
        } else {
          // Fallback to fetching from API
          fetchOrderData(orderId);
        }
      }
    } else if (demographicsId) {
      // Handle demographics route - create new order data
      const recordData = location.state?.recordData;
      
      if (recordData) {
        // Use the passed record data to initialize new order
        initializeOrderDataFromRecord(recordData);
      } else {
        // Fetch demographics data and create new order structure
        fetchDemographicsData(demographicsId);
      }
    }
  }, [orderId, demographicsId, location.state, isEditMode]);

  // Fetch products when Order Items tab is accessed
  useEffect(() => {
    if (activeTab === '2' && availableProducts.length === 0) {
      fetchAvailableProducts();
    }
    
    // Prefill incisions field when Order Items tab is accessed
    if (activeTab === '2' && templateFormData && templateFormData.locationOnBody) {
      setNewOrderItem(prev => ({
        ...prev,
        incisions: templateFormData.locationOnBody
      }));
    }
  }, [activeTab, templateFormData]);

  // Auto-select doctor when order data is loaded and fetch procedures
  useEffect(() => {
    if (orderData && orderData.patientInfo.doctorName) {
      const doctorName = orderData.patientInfo.doctorName;
      console.log('🔍 useEffect triggered - doctorName:', doctorName, 'selectedDoctor:', selectedDoctor, 'availableProcedures.length:', availableProcedures.length);
      
      // Set doctor if not already set
      if (!selectedDoctor) {
        console.log('🔍 Setting selectedDoctor to:', doctorName);
        setSelectedDoctor(doctorName);
      }
      
      // Update prescriber name with doctor name
      const currentPrescriberName = form.getFieldValue('prescriberName');
      if (!currentPrescriberName || currentPrescriberName === '') {
        console.log('🔍 Setting prescriberName to:', doctorName);
        form.setFieldsValue({ prescriberName: doctorName });
      }
      
      // Fetch procedures for the doctor if we don't have any procedures loaded yet
      // This ensures procedures are loaded in edit mode even if doctor is already selected
      if (selectedDoctor === doctorName && availableProcedures.length === 0) {
        console.log('🔍 Fetching procedures for doctor in useEffect:', doctorName);
        fetchDoctorProcedures(doctorName);
      }
    }
  }, [orderData, selectedDoctor, availableProcedures.length]);

  // Prefill incisions field with locationOnBody from template data
  useEffect(() => {
    if (templateFormData && templateFormData.locationOnBody) {
      setNewOrderItem(prev => ({
        ...prev,
        incisions: templateFormData.locationOnBody
      }));
    }
  }, [templateFormData]);

  // Prefill incisions field when Add Order Item modal opens
  useEffect(() => {
    if (isAddOrderItemModalVisible && templateFormData && templateFormData.locationOnBody) {
      setNewOrderItem(prev => ({
        ...prev,
        incisions: templateFormData.locationOnBody
      }));
    }
  }, [isAddOrderItemModalVisible, templateFormData]);

  // Prefill incisions field when Edit Order Item modal opens
  useEffect(() => {
    if (isEditOrderItemModalVisible && templateFormData && templateFormData.locationOnBody) {
      setNewOrderItem(prev => ({
        ...prev,
        incisions: templateFormData.locationOnBody
      }));
    }
  }, [isEditOrderItemModalVisible, templateFormData]);

  // Update professional designation when doctor changes
  useEffect(() => {
    if (selectedDoctor && doctors.length > 0) {
      const designation = getDoctorDesignation(selectedDoctor);
      form.setFieldsValue({ professionalDesignation: designation });
      console.log('🔍 Updated professional designation to:', designation, 'for doctor:', selectedDoctor);
    }
  }, [selectedDoctor, doctors]);

  // Fetch demographics data for new order creation
  const fetchDemographicsData = async (demographicsId: string) => {
    try {
      setLoading(true);
      
      const response = await fetch(`/api/demographics/${demographicsId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        const demographics = result.data;
        
        // Create new order data structure
        const orderFormData: OrderFormData = {
          orderId: '', // Will be created when submitted
          demographicsId: demographicsId,
          orderStatus: 'ReadyToOrder',
          orderItems: [],
          patientInfo: {
            firstName: demographics.firstName || '',
            lastName: demographics.lastName || '',
            doctorName: demographics.doctorName || '',
            procedureName: demographics.procedureName || '',
            surgeryDate: demographics.surgeryDate || '',
            dateOfBirth: demographics.dateOfBirth || '',
            gender: demographics.details?.['Current Patient Information']?.['Gender'] || ''
          },
          createdAt: new Date().toISOString(),
          lastModifiedDate: new Date().toISOString()
        };
        
        setOrderData(orderFormData);
        setFormData({ orderItems: [] });
        
        // Initialize form with default values
        form.setFieldsValue({
          orderAccuracyCertification: false,
          prescriberName: demographics.doctorName || '',
          professionalDesignation: getDoctorDesignation(demographics.doctorName || ''),
          signatureAgreement: false,
          practitionerName: '',
          certificationNotes: ''
        });
        
        // Auto-select doctor if available
        if (demographics.doctorName) {
          setSelectedDoctor(demographics.doctorName);
          fetchDoctorProcedures(demographics.doctorName);
        }
        
      } else {
        message.error('Failed to fetch demographics data');
      }
    } catch (error) {
      console.error('Error fetching demographics data:', error);
      message.error('Error fetching demographics data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize order data from passed record
  const initializeOrderDataFromRecord = (recordData: any) => {
    try {
      setLoading(true);
      
      const orderFormData: OrderFormData = {
        orderId: recordData.orderId || orderId || '',
        demographicsId: recordData.id || recordData.demographicsId || '',
        orderStatus: recordData.status || 'Ready to Order',
        orderItems: recordData.orderItems || [],
        patientInfo: {
          firstName: recordData.firstName || '',
          lastName: recordData.lastName || '',
          doctorName: recordData.doctorName || '',
          procedureName: recordData.procedureName || '',
          surgeryDate: recordData.surgeryDate || '',
          dateOfBirth: recordData.dateOfBirth || '',
          gender: recordData.details?.['Current Patient Information']?.['Gender'] || ''
        },
        createdAt: recordData.createdAt || new Date().toISOString(),
        lastModifiedDate: recordData.lastModified || new Date().toISOString()
      };

      setOrderData(orderFormData);
      setFormData(orderFormData);
      
      // Set original procedure if available
      if (recordData.procedureName) {
        setOriginalProcedure(recordData.procedureName);
      }
      
      // Initialize form with default values
      form.setFieldsValue({
        orderAccuracyCertification: false,
        prescriberName: recordData.doctorName || '',
        professionalDesignation: getDoctorDesignation(recordData.doctorName || ''),
        signatureAgreement: false,
        practitionerName: '',
        certificationNotes: ''
      });
    } catch (error) {
      console.error('Error initializing order data from record:', error);
      message.error('Error loading order data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDataForEdit = async (id: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/orders/${id}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        const order = result.data;
        
        // Fetch patient demographics data
        const demographicsResponse = await fetch(`/api/demographics/${order.demographicsId}`, {
          headers: getAuthHeaders()
        });

        if (demographicsResponse.ok) {
          const demographicsResult = await demographicsResponse.json();
          const demographics = demographicsResult.data;

          const orderFormData: OrderFormData = {
            orderId: order.orderId,
            demographicsId: order.demographicsId,
            orderStatus: order.orderStatus,
            orderItems: order.orderItems || [],
            patientInfo: {
              firstName: demographics.firstName || '',
              lastName: demographics.lastName || '',
              doctorName: demographics.doctorName || '',
              procedureName: demographics.procedureName || '',
              surgeryDate: demographics.surgeryDate || '',
              dateOfBirth: demographics.dateOfBirth || '',
              gender: demographics.details?.['Current Patient Information']?.['Gender'] || ''
            },
            createdAt: order.createdAt,
            lastModifiedDate: order.lastModifiedDate
          };

          setOrderData(orderFormData);
          setFormData(orderFormData);
          
          // Pre-fill form with existing order data for editing
          const formValues = (order as any).formValues || {};
          const orderAccuracy = order.orderAccuracy || {};
          const formDataFromOrder = (order as any).formData || {};
          
          form.setFieldsValue({
            // Order Accuracy fields
            orderAccuracyCertification: orderAccuracy.orderAccuracyCertification || false,
            prescriberName: orderAccuracy.prescriberName || formValues.prescriberName || '',
            professionalDesignation: orderAccuracy.professionalDesignation || formValues.professionalDesignation || '',
            signatureAgreement: orderAccuracy.signatureAgreement || formValues.signatureAgreement || false,
            practitionerName: orderAccuracy.practitionerName || formValues.practitionerName || '',
            certificationNotes: orderAccuracy.certificationNotes || formValues.certificationNotes || '',
            certificationDate: orderAccuracy.certificationDate || formValues.certificationDate || '',
            
            // Form values from order
            ...formValues,
            
            // Template data
            selectedDoctor: formDataFromOrder.selectedDoctor || '',
            selectedProcedure: formDataFromOrder.selectedProcedure || ''
          });

          // Set template-related state
          if (formDataFromOrder.selectedDoctor) {
            setSelectedDoctor(formDataFromOrder.selectedDoctor);
            // Fetch procedures for the doctor in edit mode
            fetchDoctorProcedures(formDataFromOrder.selectedDoctor);
          }
          if (formDataFromOrder.selectedProcedure) {
            setSelectedProcedure(formDataFromOrder.selectedProcedure);
            // Store the original procedure for comparison
            setOriginalProcedure(formDataFromOrder.selectedProcedure);
          }
          if (formDataFromOrder.templateFormData) {
            setTemplateFormData(formDataFromOrder.templateFormData);
          }
          if (formDataFromOrder.dynamicIncisionFields) {
            setDynamicIncisionFields(formDataFromOrder.dynamicIncisionFields);
          }

          // Also set form values from the order's formValues to ensure all fields are prefilled
          if (formValues && Object.keys(formValues).length > 0) {
            console.log('🔍 Setting form values for edit mode:', formValues);
            // Update form with all formValues from the order
            form.setFieldsValue(formValues);
          }

          // Set procedure template if available from formData
          if (formDataFromOrder.templateFormData) {
            // Create a procedure template object from the template form data
            const procedureTemplateData = {
              procedureName: formDataFromOrder.selectedProcedure || '',
              ...formDataFromOrder.templateFormData
            };
            setProcedureTemplate(procedureTemplateData);
            console.log('🔍 Setting procedure template for edit mode:', procedureTemplateData);
          }

          // Mark all tabs as completed since we're editing an existing order
          setCompletedTabs(new Set(['1', '2', '3']));
          
          message.success('Order data loaded for editing');
        } else {
          message.error('Failed to fetch demographics data');
        }
      } else {
        message.error('Failed to fetch order data');
      }
    } catch (error) {
      console.error('Error fetching order data for edit:', error);
      message.error('Error loading order for editing');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderData = async (id: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/orders/${id}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        const order = result.data;
        
        // Fetch patient demographics data
        const demographicsResponse = await fetch(`/api/demographics/${order.demographicsId}`, {
          headers: getAuthHeaders()
        });

        if (demographicsResponse.ok) {
          const demographicsResult = await demographicsResponse.json();
          const demographics = demographicsResult.data;

          const orderFormData: OrderFormData = {
            orderId: order.orderId,
            demographicsId: order.demographicsId,
            orderStatus: order.orderStatus,
            orderItems: order.orderItems || [],
            patientInfo: {
              firstName: demographics.firstName || '',
              lastName: demographics.lastName || '',
              doctorName: demographics.doctorName || '',
              procedureName: demographics.procedureName || '',
              surgeryDate: demographics.surgeryDate || '',
              dateOfBirth: demographics.dateOfBirth || '',
              gender: demographics.details?.['Current Patient Information']?.['Gender'] || ''
            },
            createdAt: order.createdAt,
            lastModifiedDate: order.lastModifiedDate
          };

          setOrderData(orderFormData);
          setFormData(orderFormData);
          
          // Initialize form with default values
          form.setFieldsValue({
            orderAccuracyCertification: false,
            prescriberName: demographics.doctorName || '',
            professionalDesignation: getDoctorDesignation(demographics.doctorName || ''),
            signatureAgreement: false,
            practitionerName: '',
            certificationNotes: ''
          });
        } else {
          message.error('Failed to fetch patient demographics');
        }
      } else {
        message.error('Failed to fetch order data');
        navigate('/orders');
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
      message.error('Error loading order data');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };



  const handleProcedureChange = async (procedureName: string) => {
    if (!selectedDoctor) return;
    
    try {
      setTemplateLoading(true);
      console.log('🔍 handleProcedureChange - Setting selectedProcedure to:', procedureName);
      setSelectedProcedure(procedureName);
      
      const response = await fetch(`/api/doctors/${encodeURIComponent(selectedDoctor)}/procedures/${encodeURIComponent(procedureName)}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        const template = result.data.templateData;
        setProcedureTemplate(template);
        
        // Initialize form data with template values
        const initialFormData: TemplateFormData = {};
        Object.keys(template).forEach(key => {
          if (key !== 'procedureName' && template[key] !== null && template[key] !== undefined) {
            initialFormData[key] = template[key];
          }
        });
        
        // In edit mode, check if this is the same procedure as in the database
        if (isEditMode && orderData) {
          // Use the stored original procedure for comparison
          const originalProcedureName = originalProcedure || 
                                      (orderData as any).formData?.selectedProcedure || 
                                      (orderData as any).templateData?.selectedProcedure;
          
          console.log('🔍 Comparing procedures - original:', originalProcedureName, 'selected:', procedureName);
          
          if (originalProcedureName === procedureName) {
            // Same procedure - preserve existing form values from database
            console.log('🔍 Same procedure selected, preserving existing form values');
            console.log('🔍 existingFormValues:', (orderData as any).formValues);
            console.log('🔍 existingTemplateFormData:', (orderData as any).formData?.templateFormData);
            console.log('🔍 currentFormValues:', form.getFieldsValue());
            
            // Get existing form values from multiple sources
            const existingFormValues = (orderData as any).formValues || {};
            const existingTemplateFormData = (orderData as any).formData?.templateFormData || {};
            const currentFormValues = form.getFieldsValue(); // Get current form values
            
            // Merge existing values with template defaults, prioritizing existing user input
            const mergedFormData = {
              ...initialFormData,           // Template defaults first
              ...existingTemplateFormData,  // Then existing template data
              ...existingFormValues,        // Then existing form values
              ...currentFormValues          // Finally current form values (highest priority)
            };
            
            console.log('🔍 mergedFormData before cleanup:', mergedFormData);
            
            // Remove any undefined values to keep form clean
            Object.keys(mergedFormData).forEach(key => {
              if (mergedFormData[key] === undefined) {
                delete mergedFormData[key];
              }
            });
            
            console.log('🔍 mergedFormData after cleanup:', mergedFormData);
            
            setTemplateFormData(mergedFormData);
            form.setFieldsValue(mergedFormData);
            
            // Update formData with merged values
            setFormData((prev: any) => ({
              ...prev,
              selectedDoctor,
              selectedProcedure: procedureName,
              templateFormData: mergedFormData
            }));
            
            message.success('Same procedure template selected - existing form values preserved');
          } else {
            // Different procedure - use new template defaults and clear existing values
            console.log('🔍 Different procedure selected, loading new template defaults');
            console.log('🔍 originalProcedureName:', originalProcedureName, 'selectedProcedure:', procedureName);
            
            // Clear dynamic incision fields when changing to different procedure
            setDynamicIncisionFields({});
            
            // Set new template form data
            setTemplateFormData(initialFormData);
            form.setFieldsValue(initialFormData);
            
            // Update formData with new template values
            setFormData((prev: any) => ({
              ...prev,
              selectedDoctor,
              selectedProcedure: procedureName,
              templateFormData: initialFormData,
              dynamicIncisionFields: {} // Clear dynamic fields
            }));
            
            message.success('Different procedure template selected - new default values loaded');
          }
        } else {
          // Not in edit mode - use template defaults
          setTemplateFormData(initialFormData);
          form.setFieldsValue(initialFormData);
          
          // Update formData with template values
          setFormData((prev: any) => ({
            ...prev,
            selectedDoctor,
            selectedProcedure: procedureName,
            templateFormData: initialFormData
          }));
        }

        // Prefill incisions field with locationOnBody if available
        if (template.locationOnBody) {
          setNewOrderItem(prev => ({
            ...prev,
            incisions: template.locationOnBody
          }));
        }
      } else {
        message.error('Failed to fetch procedure template');
      }
    } catch (error) {
      console.error('Error fetching procedure template:', error);
      message.error('Error loading procedure template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleTemplateFormChange = (_changedValues: any, allValues: any) => {
    setTemplateFormData(allValues);
    
    // Also update formData to ensure state is synchronized
    setFormData((prev: any) => ({
      ...prev,
      templateFormData: allValues
    }));
  };

  // Handle adding new incision field
  const handleAddIncision = () => {
    if (!newIncisionLabel.trim() || !newIncisionValue.trim()) {
      message.error('Please enter both label and value');
      return;
    }

    // Create a unique key for the incision field
    const incisionKey = `incision_${Date.now()}`;
    
    // Add to dynamic incision fields
    setDynamicIncisionFields(prev => ({
      ...prev,
      [incisionKey]: {
        label: newIncisionLabel.trim(),
        value: newIncisionValue.trim(),
        type: 'string' // Default to string type
      }
    }));

    // Add to form data
    setTemplateFormData(prev => ({
      ...prev,
      [incisionKey]: newIncisionValue.trim()
    }));

    // Also update formData to ensure state is synchronized
    setFormData((prev: any) => ({
      ...prev,
      dynamicIncisionFields: {
        ...prev.dynamicIncisionFields,
        [incisionKey]: {
          label: newIncisionLabel.trim(),
          value: newIncisionValue.trim(),
          type: 'string'
        }
      },
      templateFormData: {
        ...prev.templateFormData,
        [incisionKey]: newIncisionValue.trim()
      }
    }));

    // Update form with new field
    form.setFieldsValue({
      ...templateFormData,
      [incisionKey]: newIncisionValue.trim()
    });

    // Reset modal state
    setNewIncisionLabel('');
    setNewIncisionValue('');
    setIsAddIncisionModalVisible(false);
    
    message.success('Incision field added successfully');
  };

  // Handle removing incision field
  const handleRemoveIncision = (incisionKey: string) => {
    setDynamicIncisionFields(prev => {
      const newFields = { ...prev };
      delete newFields[incisionKey];
      return newFields;
    });

    setTemplateFormData(prev => {
      const newData = { ...prev };
      delete newData[incisionKey];
      return newData;
    });

    // Also update formData to ensure state is synchronized
    setFormData((prev: any) => {
      const newFormData = { ...prev };
      if (newFormData.dynamicIncisionFields) {
        delete newFormData.dynamicIncisionFields[incisionKey];
      }
      if (newFormData.templateFormData) {
        delete newFormData.templateFormData[incisionKey];
      }
      return newFormData;
    });

    form.setFieldsValue({
      ...templateFormData,
      [incisionKey]: undefined
    });

    message.success('Incision field removed');
  };

  // Fetch doctors from database
  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await fetch('/api/demographics/doctors', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDoctors(data.data || []);
        console.log('🔍 Fetched doctors:', data.data?.length || 0);
      } else {
        message.error('Failed to fetch doctors');
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
      message.error('Error fetching doctors');
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Get doctor's designation by name
  const getDoctorDesignation = (doctorName: string): string => {
    const doctor = doctors.find(d => d.name === doctorName);
    return doctor?.designation || 'MD'; // Default to MD if not found
  };

  // Fetch procedures for a specific doctor
  const fetchDoctorProcedures = async (doctorName: string) => {
    try {
      console.log('🔍 Fetching procedures for doctor:', doctorName);
      setTemplateLoading(true);
      const response = await fetch('/api/doctors/procedures', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ doctorName })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Procedures fetched successfully:', data.data.procedures);
        setAvailableProcedures(data.data.procedures || []);
      } else {
        console.error('Failed to fetch procedures for doctor:', doctorName);
        setAvailableProcedures([]);
      }
    } catch (error) {
      console.error('Error fetching procedures:', error);
      setAvailableProcedures([]);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Fetch available products for order items
  const fetchAvailableProducts = async () => {
    try {
      const response = await fetch('/api/products/order-items', {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableProducts(data.data || []);
      } else {
        console.error('Failed to fetch products for order items');
        setAvailableProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setAvailableProducts([]);
    }
  };

  // Handle adding new order item
  const handleAddOrderItem = async () => {
    try {
      // Validate form fields
      await form.validateFields();
      
      if (!newOrderItem.item) {
        message.error('Please select an item');
        return;
      }
      if (!newOrderItem.frequency.trim()) {
        message.error('Please enter frequency');
        return;
      }
      if (!newOrderItem.incisions.trim()) {
        message.error('Please enter incisions');
        return;
      }
    } catch (error) {
      message.error('Please fill in all required fields');
      return;
    }

    // Validate inventory before adding item
    try {
      const inventoryResponse = await fetch('/api/inventory/validate-decrement', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId: newOrderItem.item.productId,
          variantSize: newOrderItem.item.variantSize,
          requestedQuantity: newOrderItem.quantity
        })
      });

      const inventoryResult = await inventoryResponse.json();

      if (!inventoryResult.success) {
        // Inventory validation failed
        message.error(inventoryResult.message);
        return;
      }

      // Inventory validation successful, proceed with adding item
      const orderItem = {
        type: newOrderItem.type,
        item: {
          productId: newOrderItem.item.productId,
          productName: newOrderItem.item.productName,
          variantSize: newOrderItem.item.variantSize,
          productCategory: newOrderItem.item.category,
          price: Number(newOrderItem.item.price)
        },
        placeOfService: newOrderItem.placeOfService,
        quantity: newOrderItem.quantity,
        renderingProvider: newOrderItem.renderingProvider,
        frequency: newOrderItem.frequency,
        days: newOrderItem.days,
        incisions: newOrderItem.incisions
      };

      setFormData((prev: any) => ({
        ...prev,
        orderItems: [...(prev.orderItems || []), orderItem]
      }));

      // Reset form
      setNewOrderItem({
        type: 'Primary',
        item: null,
        placeOfService: '',
        quantity: 1,
        renderingProvider: '',
        frequency: '',
        days: 1,
        incisions: ''
      });

      setIsAddOrderItemModalVisible(false);
      message.success(`Order item added successfully. Remaining stock: ${inventoryResult.remainingStock}`);

    } catch (error) {
      console.error('Error validating inventory:', error);
      message.error('Error validating inventory. Please try again.');
    }
  };

  // Handle removing order item
  const handleRemoveOrderItem = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      orderItems: prev.orderItems.filter((_: any, i: number) => i !== index)
    }));
    message.success('Order item removed');
  };

  // Handle editing order item
  const handleEditOrderItem = async (index: number) => {
    const itemToEdit = formData.orderItems[index];
    
    // Restore stock before editing to maintain consistency
    try {
      const restoreStockResponse = await fetch('/api/inventory/restore-stock', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId: itemToEdit.item.productId,
          variantSize: itemToEdit.item.variantSize,
          quantity: itemToEdit.quantity
        })
      });

      const restoreResult = await restoreStockResponse.json();

      if (!restoreResult.success) {
        message.error(`Failed to restore stock: ${restoreResult.message}`);
        return;
      }

      console.log(`Stock restored: ${itemToEdit.quantity} units for product ${itemToEdit.item.productId}`);
      
    } catch (error) {
      console.error('Error restoring stock:', error);
      message.error('Error restoring stock. Please try again.');
      return;
    }

    setEditingOrderItemIndex(index);
    setNewOrderItem({
      type: itemToEdit.type,
      item: itemToEdit.item,
      placeOfService: itemToEdit.placeOfService,
      quantity: itemToEdit.quantity,
      renderingProvider: itemToEdit.renderingProvider,
      frequency: itemToEdit.frequency,
      days: itemToEdit.days,
      incisions: itemToEdit.incisions
    });
    setIsEditOrderItemModalVisible(true);
  };

  // Handle updating edited order item
  const handleUpdateOrderItem = async () => {
    try {
      // Validate form fields
      await form.validateFields();
      
      if (!newOrderItem.item) {
        message.error('Please select an item');
        return;
      }
      if (!newOrderItem.frequency.trim()) {
        message.error('Please enter frequency');
        return;
      }
      if (!newOrderItem.incisions.trim()) {
        message.error('Please enter incisions');
        return;
      }
    } catch (error) {
      message.error('Please fill in all required fields');
      return;
    }

    // Validate inventory for the updated quantity
    try {
      const inventoryResponse = await fetch('/api/inventory/validate-decrement', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId: newOrderItem.item.productId,
          variantSize: newOrderItem.item.variantSize,
          requestedQuantity: newOrderItem.quantity
        })
      });

      const inventoryResult = await inventoryResponse.json();

      if (!inventoryResult.success) {
        // Inventory validation failed
        message.error(inventoryResult.message);
        return;
      }

      // Inventory validation successful, proceed with updating item
      const updatedOrderItem = {
        type: newOrderItem.type,
        item: {
          productId: newOrderItem.item.productId,
          productName: newOrderItem.item.productName,
          variantSize: newOrderItem.item.variantSize,
          productCategory: newOrderItem.item.category,
          price: Number(newOrderItem.item.price)
        },
        placeOfService: newOrderItem.placeOfService,
        quantity: newOrderItem.quantity,
        renderingProvider: newOrderItem.renderingProvider,
        frequency: newOrderItem.frequency,
        days: newOrderItem.days,
        incisions: newOrderItem.incisions
      };

      setFormData((prev: any) => ({
        ...prev,
        orderItems: prev.orderItems.map((item: any, i: number) => 
          i === editingOrderItemIndex ? updatedOrderItem : item
        )
      }));

      // Reset form and close modal
      setNewOrderItem({
        type: 'Primary',
        item: null,
        placeOfService: '',
        quantity: 1,
        renderingProvider: '',
        frequency: '',
        days: 1,
        incisions: ''
      });
      setEditingOrderItemIndex(null);
      setIsEditOrderItemModalVisible(false);
      message.success(`Order item updated successfully. Remaining stock: ${inventoryResult.remainingStock}`);

    } catch (error) {
      console.error('Error validating inventory:', error);
      message.error('Error validating inventory. Please try again.');
    }
  };

  // Tab navigation and validation
  const handleTabChange = (key: string) => {
    const currentTabIndex = parseInt(activeTab);
    const targetTabIndex = parseInt(key);
    
    // Allow going back to previous tabs or staying on current tab
    if (targetTabIndex <= currentTabIndex) {
      setActiveTab(key);
      return;
    }
    
    // For forward navigation, validate current tab first
    if (validateCurrentTab(activeTab)) {
      setCompletedTabs(prev => new Set([...prev, activeTab]));
      setActiveTab(key);
    }
  };

  const validateCurrentTab = (tabKey: string): boolean => {
    switch (tabKey) {
      case '1': // Order Form
        if (!selectedProcedure) {
          message.error('Please select a procedure template before proceeding');
          return false;
        }
        return true;
      case '2': // Order Items
        if (!formData.orderItems || formData.orderItems.length === 0) {
          message.error('Please add at least one order item before proceeding');
          return false;
        }
        return true;
      case '3': // Certification
        // This will be validated when submitting
        return true;
      default:
        return true;
    }
  };

  const canNavigateToTab = (tabKey: string): boolean => {
    const tabIndex = parseInt(tabKey);
    const currentTabIndex = parseInt(activeTab);
    
    // Can always go back or stay on current tab
    if (tabIndex <= currentTabIndex) return true;
    
    // Can go forward if previous tabs are completed
    for (let i = 1; i < tabIndex; i++) {
      if (!completedTabs.has(i.toString())) return false;
    }
    
    return true;
  };

  // Tab content components
  const renderOrderFormTab = () => (
      <div>
        <Title level={4}>Step 1: Order Form</Title>
        <Text type="secondary">Fill in the basic order information and select procedure template</Text>
        
        <Divider />
      
      {/* Template Selection Card */}
      <Card 
        title={
          <Space>
            <FormOutlined />
            <span>Template Selection</span>
          </Space>
        } 
        style={{ marginBottom: '24px' }}
      >
        <Alert
          message={isEditMode ? "Edit Mode: You can change the procedure template. If you select the same template, existing form values will be preserved. If you select a different template, new default values will be loaded and existing values will be cleared." : "Please select a procedure to generate the order form"}
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <div>
              <Text strong>Select Procedure Template:</Text>
              <Select
                placeholder="Choose a procedure"
                style={{ width: '100%', marginTop: '8px' }}
                value={selectedProcedure}
                onChange={(value) => {
                  handleProcedureChange(value);
                  // Update formData when procedure changes
                  setFormData((prev: any) => ({
                    ...prev,
                    selectedProcedure: value
                  }));
                }}
                disabled={!selectedDoctor}
                loading={templateLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={availableProcedures.map(proc => ({
                  label: proc,
                  value: proc
                }))}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Dynamic Template Form */}
      {selectedProcedure && procedureTemplate && (
        <Card 
          title={
            <Space>
              <FileTextOutlined />
              <span>Procedure Template Form - {selectedProcedure}</span>
            </Space>
          } 
          style={{ marginBottom: '24px' }}
        >
          {templateLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <br />
              <Text type="secondary" style={{ marginTop: '16px' }}>
                Loading template form...
              </Text>
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleTemplateFormChange}
              initialValues={templateFormData}
              style={{ '--ant-form-item-label-font-size': '14px' } as any}
            >
              <Collapse defaultActiveKey={['basic', 'incision', 'wound']} ghost>
                <Panel header={<Title level={3} style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Basic Information</Title>} key="basic">
                  <Row gutter={[16, 16]}>
                    {Object.entries(procedureTemplate)
                      .filter(([key]) => 
                        ['referringDoctor','referringNPI','selectDocumentation','patientShownHow','patientProvideAlternative','patientName'].includes(key)
                      )
                      .map(([key, value]) => {
                        // Special handling for referringNPI - add diagnosis codes after it
                        if (key === 'referringNPI') {
                          return (
                            <React.Fragment key={key}>
                              <Col xs={24} sm={12} md={6}>
                                {renderTemplateField(key, value)}
                              </Col>
                              
                              {/* Diagnosis Codes Section - placed after Referring NPI */}
                              <Col xs={24}>
                                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                                  <Text strong style={{ fontSize: '16px', marginBottom: '12px', display: 'block' }}>
                                    Diagnosis Codes
                                  </Text>
                                  
                                  {/* Diagnosis Code A-D - Full Width Row */}
                                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24}>
                                      <div style={{ marginBottom: '16px' }}>
                                        <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                                          Diagnosis Code A-D
                                        </Text>
                                        <Row gutter={[8, 8]}>
                                          {[1, 2, 3, 4].map(num => (
                                            <Col xs={6} key={`diagnosisCodeAToD_${num}`}>
                                              <Form.Item
                                                name={`diagnosisCodeAToD_${num}`}
                                                style={{ marginBottom: '8px' }}
                                              >
                                                <Input placeholder={`Code ${num}`} />
                                              </Form.Item>
                                            </Col>
                                          ))}
                                        </Row>
                                      </div>
                                    </Col>
                                  </Row>
                                  
                                  {/* Diagnosis Code E-H - Full Width Row */}
                                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24}>
                                      <div style={{ marginBottom: '16px' }}>
                                        <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                                          Diagnosis Code E-H
                                        </Text>
                                        <Row gutter={[8, 8]}>
                                          {[1, 2, 3, 4].map(num => (
                                            <Col xs={6} key={`diagnosisCodeEToH_${num}`}>
                                              <Form.Item
                                                name={`diagnosisCodeEToH_${num}`}
                                                style={{ marginBottom: '8px' }}
                                              >
                                                <Input placeholder={`Code ${num}`} />
                                              </Form.Item>
                                            </Col>
                                          ))}
                                        </Row>
                                      </div>
                                    </Col>
                                  </Row>
                                  
                                  {/* Diagnosis Code I-L - Full Width Row */}
                                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24}>
                                      <div style={{ marginBottom: '16px' }}>
                                        <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                                          Diagnosis Code I-L
                                        </Text>
                                        <Row gutter={[8, 8]}>
                                          {[1, 2, 3, 4].map(num => (
                                            <Col xs={6} key={`diagnosisCodeIToL_${num}`}>
                                              <Form.Item
                                                name={`diagnosisCodeIToL_${num}`}
                                                style={{ marginBottom: '8px' }}
                                              >
                                                <Input placeholder={`Code ${num}`} />
                                              </Form.Item>
                                            </Col>
                                          ))}
                                        </Row>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                            </React.Fragment>
                          );
                        }
                        
                        // Regular field rendering for other fields
                        return (
                          <Col xs={24} sm={12} md={6} key={key}>
                            {renderTemplateField(key, value)}
                          </Col>
                        );
                      })}
                  </Row>
                </Panel>
                <Panel 
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Title level={3} style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Incision Information</Title>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => setIsAddIncisionModalVisible(true)}
                        style={{ marginLeft: '16px' }}
                      >
                        + Add Incision
                      </Button>
                    </div>
                  } 
                  key="incision"
                >
                  <Row gutter={[16, 16]}>
                    {/* Static template fields */}
                    {Object.entries(procedureTemplate)
                      .filter(([key]) => 
                        !['referringDoctor','referringNPI','selectDocumentation','patientShownHow','patientProvideAlternative','patientName','debridementType', 'cause', 'granularTissue', 'tissueAppearance', 'necroticTissue','ulcerStatus','preDebridementWoundSize(LenghtxWidthxHeight)'].includes(key)
                      )
                      .map(([key, value]) => (
                        <Col xs={24} sm={12} md={6} key={key}>
                          {renderTemplateField(key, value)}
                        </Col>
                      ))}
                    
                    {/* Dynamic incision fields */}
                    {Object.entries(dynamicIncisionFields).map(([incisionKey, incisionData]) => (
                      <Col xs={24} sm={12} md={6} key={incisionKey}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item 
                            name={incisionKey} 
                            label={<span style={{ fontSize: '14px', fontWeight: 'normal' }}>{incisionData.label}</span>}
                            style={{ marginBottom: '8px' }}
                          >
                            <Input placeholder={`Enter ${incisionData.label.toLowerCase()}`} />
                          </Form.Item>
                          <Button 
                            type="text" 
                            danger 
                            size="small"
                            onClick={() => handleRemoveIncision(incisionKey)}
                            style={{ 
                              position: 'absolute', 
                              top: '0', 
                              right: '0',
                              padding: '4px',
                              minWidth: 'auto',
                              height: 'auto'
                            }}
                            title="Remove incision field"
                          >
                            ×
                          </Button>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Panel>
                
                <Panel header={<Title level={3} style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Wound Assessment</Title>} key="wound">
                  <Row gutter={[16, 16]}>
                    {Object.entries(procedureTemplate)
                      .filter(([key]) => 
                        ['debridementType', 'cause', 'granularTissue', 'tissueAppearance', 'necroticTissue','ulcerStatus','preDebridementWoundSize(LenghtxWidthxHeight)'].includes(key)
                      )
                      .map(([key, value]) => (
                        <Col xs={24} sm={12} md={6} key={key}>
                          {renderTemplateField(key, value)}
                        </Col>
                      ))}
                  </Row>
                </Panel>
                
              </Collapse>
            </Form>
          )}
        </Card>
      )}

      {/* Navigation Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #f0f0f0'
      }}>
        <Button 
          type="primary" 
          size="large"
          onClick={() => handleTabChange('2')}
          disabled={!selectedProcedure}
        >
          Next: Order Items →
        </Button>
      </div>
    </div>
  );

  const renderOrderItemsTab = () => (
    <div>
      <Title level={4}>Step 2: Order Items</Title>
      <Text type="secondary">Add and configure the items for this order</Text>
      
      <Divider />
      
      {/* Order Items Management */}
      <Card title="Order Items" style={{ marginBottom: '24px' }}>
        {formData.orderItems && formData.orderItems.length > 0 ? (
          <div>
            {formData.orderItems.map((item: any, index: number) => (
              <Card 
                key={index} 
                size="small" 
                style={{ 
                  marginBottom: '16px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Tag color={item.type === 'Primary' ? 'blue' : item.type === 'Secondary' ? 'green' : 'orange'}>
                      {item.type}
                    </Tag>
                    <Text strong>
                      {item.item.productName}
                      {item.item.variantSize && ` (${item.item.variantSize})`}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button 
                      type="default" 
                      size="small"
                      onClick={() => handleEditOrderItem(index)}
                      style={{ 
                        padding: '4px 12px',
                        marginRight: '8px',
                        borderColor: '#1890ff',
                        color: '#1890ff',
                        backgroundColor: '#fff'
                      }}
                    >
                      Edit
                    </Button>
                    <Button 
                      type="default" 
                      danger 
                      size="small"
                      onClick={() => handleRemoveOrderItem(index)}
                      style={{ 
                        padding: '4px 12px',
                        borderColor: '#ff4d4f',
                        color: '#ff4d4f',
                        backgroundColor: '#fff'
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>

                <Row gutter={[16, 8]}>
                  <Col xs={12} sm={6}>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#fafafa', 
                      borderRadius: '4px',
                      minHeight: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Text type="secondary" style={{ marginBottom: '2px' }}>QUANTITY</Text>
                      <Text strong>{item.quantity}</Text>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#fafafa', 
                      borderRadius: '4px',
                      minHeight: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Text type="secondary" style={{ marginBottom: '2px' }}>UNIT PRICE</Text>
                      <Text strong>${Number(item.item.price)}</Text>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#fafafa', 
                      borderRadius: '4px',
                      minHeight: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Text type="secondary" style={{ marginBottom: '2px' }}>FREQUENCY</Text>
                      <Text strong>{item.frequency}</Text>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#fafafa', 
                      borderRadius: '4px',
                      minHeight: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Text type="secondary" style={{ marginBottom: '2px' }}>DAYS</Text>
                      <Text strong>{item.days}</Text>
                    </div>
                  </Col>
                </Row>

                <Row gutter={[16, 8]} style={{ marginTop: '8px' }}>
                  <Col xs={24} sm={12}>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#fafafa', 
                      borderRadius: '4px',
                      minHeight: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Text type="secondary" style={{ marginBottom: '2px' }}>PLACE OF SERVICE</Text>
                      <Text strong>{item.placeOfService || 'Not specified'}</Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#fafafa', 
                      borderRadius: '4px',
                      minHeight: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Text type="secondary" style={{ marginBottom: '2px' }}>RENDERING PROVIDER</Text>
                      <Text strong>{item.renderingProvider || 'Not specified'}</Text>
                    </div>
                  </Col>
                </Row>

                {item.incisions && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#f6f8fa', 
                      borderRadius: '4px',
                      border: '1px solid #e1e4e8'
                    }}>
                      <Text type="secondary" style={{ marginBottom: '4px', display: 'block' }}>INCISIONS</Text>
                      <Text style={{ lineHeight: '1.4' }}>{item.incisions}</Text>
                    </div>
                  </div>
                )}

                <div style={{ 
                  marginTop: '12px', 
                  padding: '12px', 
                  backgroundColor: '#e6f7ff', 
                  borderRadius: '4px',
                  border: '1px solid #91d5ff',
                  textAlign: 'right'
                }}>
                  <Text type="secondary" style={{ marginRight: '8px' }}>TOTAL AMOUNT:</Text>
                  <Text strong style={{ color: '#1890ff' }}>
                    ${(Number(item.item.price) * item.quantity).toFixed(2)}
                  </Text>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Empty 
            description="No order items added yet" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
        
        <Button 
          type="dashed" 
          onClick={() => setIsAddOrderItemModalVisible(true)}
          style={{ width: '100%', marginTop: '16px' }}
        >
          + Add Order Item
        </Button>
      </Card>
      
      {/* Add Order Item Modal */}
      <Modal
        title="Add Order Item"
        open={isAddOrderItemModalVisible}
        onOk={handleAddOrderItem}
        onCancel={() => {
          setIsAddOrderItemModalVisible(false);
          setNewOrderItem({
            type: 'Primary',
            item: null,
            placeOfService: '',
            quantity: 1,
            renderingProvider: '',
            frequency: '',
            days: 1,
            incisions: templateFormData?.locationOnBody || ''
          });
        }}
        okText="Add Item"
        cancelText="Cancel"
        width={800}
      >
        <Form layout="vertical" form={form}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item 
                label="Type" 
                name="type"
                rules={[{ required: true, message: 'Please select a type' }]}
                initialValue="Primary"
              >
                <Select
                  value={newOrderItem.type}
                  onChange={(value) => setNewOrderItem(prev => ({ ...prev, type: value }))}
                >
                  <Select.Option value="Primary">Primary</Select.Option>
                  <Select.Option value="Secondary">Secondary</Select.Option>
                  <Select.Option value="Ancillary">Ancillary</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Item" required>
                <Select
                  placeholder="Select an item"
                  value={newOrderItem.item?.productId}
                  onChange={(value) => {
                    const selectedItem = availableProducts.find(p => p.productId === value);
                    setNewOrderItem(prev => ({ ...prev, item: selectedItem }));
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={availableProducts.map(product => ({
                    label: product.displayName,
                    value: product.productId
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Place of Service">
                <Input
                  value={newOrderItem.placeOfService}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, placeOfService: e.target.value }))}
                  placeholder="Enter place of service"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Quantity" required>
                <InputNumber
                  value={newOrderItem.quantity}
                  onChange={(value) => setNewOrderItem(prev => ({ ...prev, quantity: value || 1 }))}
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Rendering Provider">
                <Input
                  value={newOrderItem.renderingProvider}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, renderingProvider: e.target.value }))}
                  placeholder="Enter rendering provider"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Frequency" required>
                <Input
                  value={newOrderItem.frequency}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, frequency: e.target.value }))}
                  placeholder="Enter frequency"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Days" required>
                <InputNumber
                  value={newOrderItem.days}
                  onChange={(value) => setNewOrderItem(prev => ({ ...prev, days: value || 1 }))}
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Incisions" required>
                <Input
                  value={newOrderItem.incisions}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, incisions: e.target.value }))}
                  placeholder="Enter incisions information"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Edit Order Item Modal */}
      <Modal
        title="Edit Order Item"
        open={isEditOrderItemModalVisible}
        onOk={handleUpdateOrderItem}
        onCancel={() => {
          setIsEditOrderItemModalVisible(false);
          setEditingOrderItemIndex(null);
          setNewOrderItem({
            type: 'Primary',
            item: null,
            placeOfService: '',
            quantity: 1,
            renderingProvider: '',
            frequency: '',
            days: 1,
            incisions: templateFormData?.locationOnBody || ''
          });
        }}
        okText="Update Item"
        cancelText="Cancel"
        width={800}
      >
        <Form layout="vertical" form={form}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item 
                label="Type" 
                name="type"
                rules={[{ required: true, message: 'Please select a type' }]}
                initialValue="Primary"
              >
                <Select
                  value={newOrderItem.type}
                  onChange={(value) => setNewOrderItem(prev => ({ ...prev, type: value }))}
                >
                  <Select.Option value="Primary">Primary</Select.Option>
                  <Select.Option value="Secondary">Secondary</Select.Option>
                  <Select.Option value="Ancillary">Ancillary</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Item" required>
                <Select
                  placeholder="Select an item"
                  value={newOrderItem.item?.productId}
                  onChange={(value) => {
                    const selectedItem = availableProducts.find(p => p.productId === value);
                    setNewOrderItem(prev => ({ ...prev, item: selectedItem }));
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={availableProducts.map(product => ({
                    label: product.displayName,
                    value: product.productId
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Place of Service">
                <Input
                  value={newOrderItem.placeOfService}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, placeOfService: e.target.value }))}
                  placeholder="Enter place of service"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Quantity" required>
                <InputNumber
                  value={newOrderItem.quantity}
                  onChange={(value) => setNewOrderItem(prev => ({ ...prev, quantity: value || 1 }))}
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Rendering Provider">
                <Input
                  value={newOrderItem.renderingProvider}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, renderingProvider: e.target.value }))}
                  placeholder="Enter rendering provider"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Frequency" required>
                <Input
                  value={newOrderItem.frequency}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, frequency: e.target.value }))}
                  placeholder="Enter frequency"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Days" required>
                <InputNumber
                  value={newOrderItem.days}
                  onChange={(value) => setNewOrderItem(prev => ({ ...prev, days: value || 1 }))}
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Incisions" required>
                <Input
                  value={newOrderItem.incisions}
                  onChange={(e) => setNewOrderItem(prev => ({ ...prev, incisions: e.target.value }))}
                  placeholder="Enter incisions information"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      
      {/* Navigation Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
        <Button 
          size="large" 
          onClick={() => handleTabChange('1')}
        >
          ← Previous: Order Form
        </Button>
        <Button 
          type="primary" 
          size="large" 
          onClick={() => handleTabChange('3')}
          disabled={!formData.orderItems || formData.orderItems.length === 0}
        >
          Next: Certification →
        </Button>
      </div>
    </div>
  );

  const renderCertificationTab = () => (
    <div>
      <Title level={4}>Step 3: Certification by Practitioner</Title>
    
      <Divider />
      
      {/* Order Accuracy Section */}
      <Card title="Order Accuracy" style={{ marginBottom: '24px' }}>
        <Form layout="vertical" form={form}>
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Form.Item
                name="orderAccuracyCertification"
                valuePropName="checked"
                rules={[{ required: true, message: 'You must certify the order accuracy' }]}
              >
                <Checkbox>
                  I certify that the information provided in this order is accurate and complete to the best of my knowledge.
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Prescriber Name"
                name="prescriberName"
                rules={[{ required: true, message: 'Please enter prescriber name' }]}
              >
                <Input placeholder="Enter prescriber name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Professional Designation"
                name="professionalDesignation"
                rules={[{ required: true, message: 'Please enter professional designation' }]}
              >
                <Input placeholder="Enter designation (MD, DO, DPM, etc.)" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f6f8fa', 
                borderRadius: '6px',
                border: '1px solid #e1e4e8',
                marginBottom: '16px'
              }}>
                <Text>
                  Physician Approval: I certify that I am treating the patient named above and am ordering these supplies based on my exam and treatment of the patient.
                  I affirm the supplies are medically reasonable and necessary. Please keep a copy of the patient's chart.
                  </Text>
              </div>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f6f8fa', 
                borderRadius: '6px',
                border: '1px solid #e1e4e8',
                marginBottom: '16px'
              }}>
                <Text>
                  The products and/or services provided to you are subject to the supplier standards contained in the Federal Regulations shown at 42 Code of the Federal Regulation Section 424.57(c). These standards concern business professional and
                  operational matters (e.g. honoring warrenties and hours of operations). the full text of these standards can be obtained at http://ecfr.gpoaccess.gov. (Form 051916-1)
                  </Text>
              </div>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Form.Item
                name="signatureAgreement"
                valuePropName="checked"
                rules={[{ required: true, message: 'You must agree to the signature terms' }]}
              >
                <Checkbox required>
                  I agree. I am signing this document electronically. I agree that my electronic signature is the legal equivalent of my handwritten signature on this document. </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
      
      

      

      {/* Navigation Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #f0f0f0'
      }}>
        <Button 
          size="large"
          onClick={() => handleTabChange('2')}
        >
          ← Previous: Order Items
        </Button>
        <Button 
          type="primary" 
          size="large"
          onClick={handleSubmit}
          loading={saving}
        >
          {isEditMode ? 'Update Order' : 'Submit Order'}
        </Button>
      </div>
    </div>
  );

  // Helper function to render form fields based on template data
  const renderTemplateField = (key: string, value: any) => {
    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    const labelStyle = { fontSize: '14px', fontWeight: 'normal' };
    
    // Determine field type based on value
    if (typeof value === 'boolean') {
      return (
        <Form.Item key={key} name={key} label={<span style={labelStyle}>{label}</span>} valuePropName="checked">
          <Switch />
        </Form.Item>
      );
    } else if (typeof value === 'number') {
      return (
        <Form.Item key={key} name={key} label={<span style={labelStyle}>{label}</span>}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      );
    } else if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return (
        <Form.Item key={key} name={key} label={<span style={labelStyle}>{label}</span>}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      );
    } else {
      return (
        <Form.Item key={key} name={key} label={<span style={labelStyle}>{label}</span>}>
          <Input placeholder={`Enter ${label.toLowerCase()}`} />
        </Form.Item>
      );
    }
  };

  const handleBack = () => {
    navigate('/orders');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate form fields including certification tab
      try {
        await form.validateFields(['orderAccuracyCertification', 'prescriberName', 'professionalDesignation', 'signatureAgreement']);
      } catch (error) {
        console.log('🔍 Validation error:', error);
        message.error('Please fill in all required fields in the certification tab');
        return;
      }
      
      // Additional manual validation for checkboxes as fallback
      const validationFormValues = form.getFieldsValue();
      if (!validationFormValues.orderAccuracyCertification) {
        message.error('You must certify the order accuracy');
        return;
      }
      if (!validationFormValues.signatureAgreement) {
        message.error('You must agree to the signature terms');
        return;
      }
      if (!validationFormValues.prescriberName || validationFormValues.prescriberName.trim() === '') {
        message.error('Please enter prescriber name');
        return;
      }
      if (!validationFormValues.professionalDesignation || validationFormValues.professionalDesignation.trim() === '') {
        message.error('Please enter professional designation');
        return;
      }
      
      // Check if order already exists for this demographics ID
      const demographicsId = orderData?.demographicsId;
      const existingOrderResponse = await fetch(`/api/orders/demographics/${demographicsId}/existing`, {
        headers: getAuthHeaders()
      });
      
      let currentOrderId = orderId;
      
      if (existingOrderResponse.ok) {
        // Existing order found, use that order ID
        const existingOrderData = await existingOrderResponse.json();
        currentOrderId = existingOrderData.data.orderId;
        message.info('Existing order found, updating with new information');
      }
      
      // Get form values including Order Accuracy data
      const formValues = form.getFieldsValue();
      const currentDate = new Date().toISOString();
      
      // Merge template data with form values to ensure all prefilled values are included
      const mergedFormValues = {
        ...templateFormData,  // Include all template values first
        ...formValues,        // Override with any user-entered values
        // Ensure Order Accuracy fields are always included
        orderAccuracyCertification: formValues.orderAccuracyCertification,
        prescriberName: formValues.prescriberName,
        professionalDesignation: formValues.professionalDesignation,
        signatureAgreement: formValues.signatureAgreement,
        practitionerName: formValues.practitionerName,
        certificationNotes: formValues.certificationNotes
      };
      
      // Debug the data being sent in handleSave
      console.log('🔍 handleSave - Procedure Name Debug:', {
        selectedProcedure: selectedProcedure,
        selectedDoctor: selectedDoctor,
        templateFormData: templateFormData
      });
      
      // Update order data with template information and Order Accuracy
      const response = await fetch(`/api/orders/${currentOrderId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          orderStatus: 'ReadyToOrder', // Keep as draft status
          orderItems: formData.orderItems,
          templateData: {
            selectedDoctor,
            selectedProcedure,
            templateFormData,
            dynamicIncisionFields
          },
          orderAccuracy: {
            certificationDate: currentDate,
            orderAccuracyCertification: mergedFormValues.orderAccuracyCertification,
            prescriberName: mergedFormValues.prescriberName,
            professionalDesignation: mergedFormValues.professionalDesignation,
            signatureAgreement: mergedFormValues.signatureAgreement,
            practitionerName: mergedFormValues.practitionerName,
            certificationNotes: mergedFormValues.certificationNotes
          },
          formData: {
            selectedDoctor,
            selectedProcedure,
            templateFormData,
            dynamicIncisionFields,
            patientInfo: {
              ...(orderData?.patientInfo || {}),
              procedureName: selectedProcedure || orderData?.patientInfo?.procedureName || ''
            },
            orderItems: formData.orderItems
          },
          formValues: mergedFormValues  // Include merged form values
        })
      });

      if (response.ok) {
        message.success('Order saved successfully');
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to save order');
      }
    } catch (error) {
      console.error('Error saving order:', error);
      message.error('Error saving order');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      // Validate form fields including certification tab
      try {
        await form.validateFields(['orderAccuracyCertification', 'prescriberName', 'professionalDesignation', 'signatureAgreement']);
      } catch (error) {
        console.log('🔍 Validation error:', error);
        message.error('Please fill in all required fields in the certification tab');
        return;
      }
      
      // Additional manual validation for checkboxes as fallback
      const validationFormValues = form.getFieldsValue();
      if (!validationFormValues.orderAccuracyCertification) {
        message.error('You must certify the order accuracy');
        return;
      }
      if (!validationFormValues.signatureAgreement) {
        message.error('You must agree to the signature terms');
        return;
      }
      if (!validationFormValues.prescriberName || validationFormValues.prescriberName.trim() === '') {
        message.error('Please enter prescriber name');
        return;
      }
      if (!validationFormValues.professionalDesignation || validationFormValues.professionalDesignation.trim() === '') {
        message.error('Please enter professional designation');
        return;
      }
      
      // Validate that a procedure is selected
      if (!selectedProcedure) {
        message.error('Please select a procedure template before submitting');
        return;
      }
      
      // Validate that order items are present
      if (!formData.orderItems || formData.orderItems.length === 0) {
        message.error('Please add at least one order item before submitting');
        return;
      }
      
      const demographicsId = orderData?.demographicsId;
      let currentOrderId = orderId;
      
      // If no orderId exists, create a new order first
      if (!currentOrderId) {
        const orderData = {
          demographicsId: demographicsId,
          orderItems: formData.orderItems
        };

        const createResponse = await fetch('/api/orders', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(orderData)
        });

        if (createResponse.ok) {
          const createResult = await createResponse.json();
          currentOrderId = createResult.data?.orderId || createResult.orderId;
          message.success('Order created successfully!');
        } else {
          const errorData = await createResponse.json();
          message.error(`Failed to create order: ${errorData.message || 'Unknown error'}`);
          return;
        }
      }
      
      // Get form values including Order Accuracy data
      const formValues = form.getFieldsValue();
      const currentDate = new Date().toISOString();
      
      // Debug logging for checkbox values
      console.log('🔍 Form Values Debug in handleSubmit:', {
        orderAccuracyCertification: formValues.orderAccuracyCertification,
        signatureAgreement: formValues.signatureAgreement,
        allFormValues: formValues
      });
      
      // Merge template data with form values to ensure all prefilled values are included
      const mergedFormValues = {
        ...templateFormData,  // Include all template values first
        ...formValues,        // Override with any user-entered values
        // Ensure Order Accuracy fields are always included
        orderAccuracyCertification: formValues.orderAccuracyCertification,
        prescriberName: formValues.prescriberName,
        professionalDesignation: formValues.professionalDesignation,
        signatureAgreement: formValues.signatureAgreement,
        practitionerName: formValues.practitionerName,
        certificationNotes: formValues.certificationNotes
      };
      
      // Prepare the complete form data to send
      const completeFormData = {
        selectedDoctor,
        selectedProcedure,
        templateFormData,
        dynamicIncisionFields,
        patientInfo: {
          ...(orderData?.patientInfo || {}),
          // Ensure procedureName is set to the selected procedure
          procedureName: selectedProcedure || orderData?.patientInfo?.procedureName || ''
        },
        orderItems: formData.orderItems
      };

      const requestPayload = {
        orderStatus: 'YetToBeShipped',
        orderItems: formData.orderItems,
        templateData: {
          selectedDoctor,
          selectedProcedure,
          templateFormData,
          dynamicIncisionFields
        },
        orderAccuracy: {
          certificationDate: currentDate,
          orderAccuracyCertification: mergedFormValues.orderAccuracyCertification,
          prescriberName: mergedFormValues.prescriberName,
          professionalDesignation: mergedFormValues.professionalDesignation,
          signatureAgreement: mergedFormValues.signatureAgreement,
          practitionerName: mergedFormValues.practitionerName,
          certificationNotes: mergedFormValues.certificationNotes
        },
        formData: completeFormData,
        formValues: mergedFormValues  // Use merged form values instead of just formValues
      };

      // Log the complete payload being sent to backend
      console.log('🚀 Sending order submission payload:', {
        orderId: currentOrderId,
        payload: requestPayload,
        formValues: mergedFormValues,
        originalFormValues: formValues,
        templateFormData: templateFormData,
        demographics: orderData?.patientInfo,
        orderData: orderData,
        selectedDoctor: selectedDoctor,
        selectedProcedure: selectedProcedure,
        dynamicIncisionFields: dynamicIncisionFields,
        formData: formData,
        orderItems: formData.orderItems,
        orderItemsLength: formData.orderItems?.length || 0
      });
      
      // Specifically log the procedure name being sent
      console.log('🔍 Procedure Name Debug:', {
        selectedProcedure: selectedProcedure,
        'requestPayload.templateData.selectedProcedure': requestPayload.templateData.selectedProcedure,
        'completeFormData.selectedProcedure': completeFormData.selectedProcedure,
        'completeFormData.patientInfo.procedureName': completeFormData.patientInfo.procedureName
      });
      
      // Update order with template data and Order Accuracy
      const response = await fetch(`/api/orders/${currentOrderId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestPayload)
      });

      if (response.ok) {
        if (isEditMode) {
          message.success('Order updated successfully');
        } else {
          message.success('Order submitted successfully');
        }
        navigate('/orders');
      } else {
        const errorData = await response.json();
        message.error(errorData.message || `Failed to ${isEditMode ? 'update' : 'submit'} order`);
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      message.error('Error submitting order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!orderData) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            <Title level={3}>Order Not Found</Title>
            <Text type="secondary">The requested order could not be found.</Text>
            <br />
            <Button type="primary" onClick={handleBack} style={{ marginTop: '16px' }}>
              Back to Orders
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '24px',
        gap: '16px'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          type="text"
        >
          Back
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          {isEditMode ? 'Edit Order' : 'Order Form'} - {orderData.orderId || `MPM-${orderData.demographicsId}`}
        </Title>
      </div>

      {/* Patient Information Card */}
      <Card title="Patient Information" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Patient Name:</Text>
              <br />
              <Text>{orderData.patientInfo.firstName} {orderData.patientInfo.lastName}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Doctor:</Text>
              <br />
              <Text>{orderData.patientInfo.doctorName}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Procedure Template:</Text>
              <br />
              <Text>{orderData.patientInfo.procedureName}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Surgery Date:</Text>
              <br />
              <Text>
                {orderData.patientInfo.surgeryDate 
                  ? new Date(orderData.patientInfo.surgeryDate).toLocaleDateString()
                  : 'Not set'
                }
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Main Order Process Tabs */}
      <Card style={{ marginBottom: '24px' }}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: '1',
              label: (
                <span>
                  <FormOutlined />
                  Step 1: Order Form
                  {completedTabs.has('1') && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: '8px' }} />}
                </span>
              ),
              children: renderOrderFormTab(),
              disabled: !canNavigateToTab('1')
            },
            {
              key: '2',
              label: (
                <span>
                  <FileTextOutlined />
                  Step 2: Order Items
                  {completedTabs.has('2') && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: '8px' }} />}
                </span>
              ),
              children: renderOrderItemsTab(),
              disabled: !canNavigateToTab('2')
            },
            {
              key: '3',
              label: (
                <span>
                  <SendOutlined />
                  Step 3: Certification
                  {completedTabs.has('3') && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: '8px' }} />}
                </span>
              ),
              children: renderCertificationTab(),
              disabled: !canNavigateToTab('3')
            }
          ]}
        />
      </Card>


      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: '12px',
        padding: '16px 0',
        borderTop: '1px solid #f0f0f0'
      }}>
        <Button 
          onClick={handleBack}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button 
          type="default"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
        >
          Save Draft
        </Button>
      </div>

      {/* Add Incision Modal */}
      <Modal
        title="Add Incision Field"
        open={isAddIncisionModalVisible}
        onOk={handleAddIncision}
        onCancel={() => {
          setIsAddIncisionModalVisible(false);
          setNewIncisionLabel('');
          setNewIncisionValue('');
        }}
        okText="Add"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Label:
          </label>
          <Input
            placeholder="Enter field label (e.g., Incision Length, Incision Depth)"
            value={newIncisionLabel}
            onChange={(e) => setNewIncisionLabel(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Default Value:
          </label>
          <Input
            placeholder="Enter default value"
            value={newIncisionValue}
            onChange={(e) => setNewIncisionValue(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default SubmitOrderPage;
